import React, { useState, useMemo } from 'react';
import MainNav from './components/MainNav';
import Footer from './components/Footer';
import HomeSection from './components/HomeSection';
import WhySection from './components/WhySection';
import Gallery from './components/Gallery';
import UsageSection from './components/UsageSection';
import DownloadsSection from './components/DownloadsSection';
import AboutSimple from './components/AboutSimple';
import TestimonialsSection from './components/TestimonialsSection';
import BackToTop from './components/BackToTop';
// import GallerySection from './components/GallerySection';
import cropData from './data/cropData';
import { translations } from './constants/translations';
import { useLanguage } from './hooks/useLanguage';
import { useFilteredCrops } from './hooks/useFilteredCrops';
import { useDocumentHead } from './hooks/useDocumentHead';
import { useWelcomeSpeech } from './hooks/useWelcomeSpeech';
import PresetPage from './Pages/PresetPage';
import { PAGE_STATES } from './constants/appConfig';

function App() {
  const [language, setLanguage] = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(PAGE_STATES.HOME);

  // Keep SEO <head> tags (title, description, lang) in sync with the SPA state.
  useDocumentHead(language, currentPage);

  // Speak a spoken welcome message on first load in the selected language.
  // While it speaks, highlight the language button in the nav.
  const isWelcomeSpeaking = useWelcomeSpeech(language);

  const { categories, filteredCrops } = useFilteredCrops(cropData, searchTerm, selectedCategory);
  const t = useMemo(() => translations[language], [language]);

  const handleNavigate = (page) => {
    if (page === 'services') {
      setCurrentPage(PAGE_STATES.PRESET);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setCurrentPage(PAGE_STATES.HOME);
    
    if (page === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // For other sections, scroll to them
    setTimeout(() => {
      const targetId = page;
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <MainNav
          language={language}
          onLanguageChange={setLanguage}
          onNavigate={handleNavigate}
          currentPage={currentPage}
          highlightLanguage={isWelcomeSpeaking}
        />
      </div>

      {/* Add padding top to account for fixed header */}
      <main className="pt-[72px]">
        {currentPage === PAGE_STATES.HOME ? (
          <>
            {/* Scrollable sections with snap */}
            <div className="scroll-smooth">
              <HomeSection language={language} />
              <WhySection language={language} />
              <Gallery language={language} />
              <UsageSection language={language} />
              <DownloadsSection language={language} onNavigate={handleNavigate} />
              <AboutSimple language={language} />
              <TestimonialsSection language={language} />
              {/* <GallerySection /> */}
              <Footer language={language} totalCrops={cropData.length} onNavigate={handleNavigate} />
            </div>
          </>
        ) : (
          <PresetPage
            language={language}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={categories}
            filteredCrops={filteredCrops}
            translations={t}
          />
        )}
      </main>

      {/* Floating back-to-top button */}
      <BackToTop language={language} />

      <style>{`
        /* Smooth scroll behavior */
        html {
          scroll-behavior: smooth;
        }

        /* Snap scrolling for sections */
        @media (min-width: 768px) {
          .scroll-smooth > section {
            scroll-snap-align: start;
          }
        }

        /* Animation classes */
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fadeIn { 
          animation: fadeIn 0.6s ease-out forwards; 
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 10px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        ::-webkit-scrollbar-thumb {
          background: #10b981;
          border-radius: 5px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #059669;
        }
      `}</style>
    </div>
  );
}

export default App;
